/* ── AI Space Design Tokens — Stripe/Vercel editorial-tech ──
   Scope: opt-in via className="ai-themed" on AI Space page wrappers ONLY
          (Injections PR1; Analytics + AutoB2026 follow in PR2/PR3).
   Namespace: --ai-* (isolated from existing tokens to avoid collisions
   with darkModeCSS attribute-inversion legacy system).
   B1 SCOPE GUARANTEE: never on body, never on shared components
   (BlitzHeader, MobileNav, modals, toasts). Transition cascade only
   affects AI Space descendants.
   See brief_injections_stripe_redesign_2026_05_23.md §3 for full rationale.
*/
export const injectionsThemeCSS = `
  :root {
    /* DEFAULT = LIGHT theme tokens (Stripe/Vercel light look) */

    /* Surfaces */
    --ai-bg:            #ffffff;
    --ai-bg-soft:       #fafafa;
    --ai-page-bg:       #e9ecf0;  /* серый фон под парящей autoinject-панелью (redesign-2026-06-18) */
    --ai-surface:       #ffffff;
    --ai-surface-2:     #f4f4f5;
    --ai-surface-hover: #ededf0;

    /* Borders */
    --ai-border:        rgba(0,0,0,0.06);
    --ai-border-strong: rgba(0,0,0,0.10);
    --ai-border-focus:  rgba(124,58,237,0.40);

    /* Text (L1 Rev 2: --ai-text-muted #71717a → #6a6a73 for WCAG AA margin) */
    --ai-text:       #000000;
    --ai-text-dim:   #52525b;
    --ai-text-muted: #6a6a73;

    /* Brand accent (Stripe-like violet→pink gradient + solid emerald for Injections) */
    --ai-accent:       #6d28d9;
    --ai-accent-2:     #7c3aed;
    --ai-accent-grad:  linear-gradient(135deg, #6d28d9 0%, #db2777 100%);
    --ai-accent-soft:  rgba(109,40,217,0.08);
    --ai-accent-strong:rgba(109,40,217,0.20);

    /* Injections page-specific accent (emerald — distinguishes from Analytics violet) */
    --ai-inj:        #059669;
    --ai-inj-soft:   rgba(5,150,105,0.08);
    --ai-inj-strong: rgba(5,150,105,0.22);

    /* Status colors */
    --ai-red:    #e11d48;
    --ai-red-soft: rgba(225,29,72,0.07);
    --ai-amber:  #ca8a04;
    --ai-amber-soft: rgba(202,138,4,0.08);
    --ai-green:  #059669;
    --ai-green-soft: rgba(5,150,105,0.10);

    /* Unified neutral bar fill (PR-POLISH3 2026-05-23, brightened FIX4 2026-05-23).
       User feedback after deploy: #cbd5e1 was too subtle — bars almost invisible.
       Bumped к #64748b (slate-500) — clearly readable on white but still neutral. */
    --ai-bar-neutral: #64748b;

    /* Effects */
    --ai-shadow-sm: 0 1px 2px rgba(15,23,42,0.04);
    --ai-shadow-md: 0 4px 16px rgba(15,23,42,0.06);
    --ai-shadow-lg: 0 16px 40px rgba(15,23,42,0.10);
    --ai-shadow-hero: 0 30px 80px rgba(15,23,42,0.10), 0 12px 30px rgba(15,23,42,0.06);

    /* Premium floating shadow aliases (etalon redesign-2026-06-18, PRESETS.floating) */
    --ai-sh-card:   0 2px 6px rgba(18,28,42,.05),0 26px 54px -22px rgba(18,28,42,.20);
    --ai-sh-card-h: 0 4px 10px rgba(18,28,42,.07),0 34px 66px -22px rgba(18,28,42,.28);
    --ai-sh-pop:    0 1px 3px rgba(18,28,42,.06),0 40px 80px -30px rgba(18,28,42,.40);
    --ai-sh-win:    0 2px 8px rgba(18,28,42,.05),0 44px 96px -42px rgba(18,28,42,.28);

    /* Motion */
    --ai-ease: cubic-bezier(0.4, 0, 0.2, 1);
    --ai-ease-spring: cubic-bezier(0.34, 1.56, 0.64, 1);
    --ai-dur-fast: 150ms;
    --ai-dur-base: 220ms;
    --ai-dur-slow: 320ms;

    /* Radius */
    --ai-r-sm: 8px;
    --ai-r-md: 12px;
    --ai-r-lg: 18px;
    --ai-r-xl: 22px;

    /* Typography */
    --ai-font-sans: 'Geist', 'Inter', system-ui, -apple-system, sans-serif;
    --ai-font-mono: 'Geist Mono', 'JetBrains Mono', 'SF Mono', Menlo, monospace;
  }

  /* DARK theme override — applied via .dark-mode class on root (existing convention) */
  .dark-mode {
    --ai-bg:            #000000;
    --ai-bg-soft:       #0d0d12;
    --ai-page-bg:       #070910;  /* тёмно-серый фон под парящей autoinject-панелью */
    --ai-surface:       #15151c;
    --ai-surface-2:     #1f1f2a;
    --ai-surface-hover: #2a2a38;

    --ai-border:        rgba(255,255,255,0.08);
    --ai-border-strong: rgba(255,255,255,0.14);
    --ai-border-focus:  rgba(167,139,250,0.55);

    --ai-text:       #ffffff;
    --ai-text-dim:   #a1a1aa;
    /* Adversarial review finding: #71717a on #000 = 4.35:1 (below WCAG AA 4.5:1
       для small body text). Bumped к #86868f = 5.31:1 — safe margin. */
    --ai-text-muted: #86868f;

    --ai-accent:       #7c3aed;
    --ai-accent-2:     #a78bfa;
    --ai-accent-grad:  linear-gradient(135deg, #7c3aed 0%, #ec4899 100%);
    --ai-accent-soft:  rgba(124,58,237,0.14);
    --ai-accent-strong:rgba(124,58,237,0.32);

    --ai-inj:        #10b981;
    --ai-inj-soft:   rgba(16,185,129,0.12);
    --ai-inj-strong: rgba(16,185,129,0.32);

    --ai-red:    #f43f5e;
    --ai-red-soft: rgba(244,63,94,0.12);
    --ai-amber:  #facc15;
    --ai-amber-soft: rgba(250,204,21,0.10);
    --ai-green:  #34d399;
    --ai-green-soft: rgba(52,211,153,0.12);

    /* Unified neutral bar fill — dark variant (PR-POLISH3 2026-05-23, brightened FIX4 2026-05-23).
       User feedback after deploy: #3a3a4a almost invisible on dark surface. Bumped к
       #6b7280 (slate-500) — clearly readable on dark bg, still neutral grey. */
    --ai-bar-neutral: #6b7280;

    --ai-shadow-sm: 0 1px 0 rgba(255,255,255,0.04) inset;
    --ai-shadow-md: 0 8px 24px rgba(0,0,0,0.4);
    --ai-shadow-lg: 0 20px 60px rgba(0,0,0,0.6);
    --ai-shadow-hero: 0 30px 80px rgba(0,0,0,0.6), 0 12px 30px rgba(0,0,0,0.4);

    /* Premium floating shadow aliases — dark variant (etalon [data-theme=dark] block) */
    --ai-sh-card:   0 1px 2px rgba(0,0,0,.5),0 26px 54px -22px rgba(0,0,0,.65);
    --ai-sh-card-h: 0 4px 10px rgba(0,0,0,.55),0 34px 66px -22px rgba(0,0,0,.70);
    --ai-sh-pop:    0 30px 80px -28px rgba(0,0,0,.8);
    --ai-sh-win:    0 2px 8px rgba(0,0,0,.5),0 44px 96px -42px rgba(0,0,0,.70);
  }

  /* ── Авто-инжект: точное соответствие утверждённому макету (redesign-2026-06-18,
     пресет «Парящая»). Scoped CSS-var override на обёртке вкладки: Plus Jakarta Sans
     + JetBrains Mono + палитра мокапа (серые / статусы / surfaces / -text варианты).
     Вкладка Persistent Failed НЕ затронута — переменные переопределяются только
     внутри .ai-autoinject-scope. */
  .ai-autoinject-scope {
    --ai-font-sans: 'Plus Jakarta Sans', system-ui, -apple-system, sans-serif;
    --ai-font-mono: 'JetBrains Mono', 'SFMono-Regular', monospace;
    font-family: var(--ai-font-sans);
    --ai-text: #14171D; --ai-text-dim: #6F7884; --ai-text-muted: #9AA1AC; --ai-faint: #C2C7CF;
    --ai-surface: #FBFCFD; --ai-card: #FFFFFF; --ai-surface-2: #F2F4F8; --ai-surface-hover: #ECEFF3;
    --ai-inj: #0EA877; --ai-inj-soft: #E5F7EF; --ai-inj-text: #097A53;
    --ai-amber: #E59F2E; --ai-amber-soft: #FBF0DA; --ai-amber-text: #8F5E12;
    --ai-red: #E0524F; --ai-red-soft: #FBE8E8; --ai-red-text: #B23636;
    --ai-accent: #6E5DE0; --ai-accent-soft: #ECEDFB; --ai-accent-text: #4A4FB0;
    --ai-gray: #AEB4BE;
  }
  .dark-mode .ai-autoinject-scope {
    --ai-text: #E8ECF2; --ai-text-dim: #8C95A2; --ai-text-muted: #69727E; --ai-faint: #3C4452;
    --ai-surface: #11151C; --ai-card: #181D26; --ai-surface-2: #1A1F28; --ai-surface-hover: #232A35;
    --ai-inj: #2DD4A7; --ai-inj-soft: rgba(45,212,167,.15); --ai-inj-text: #6FE7C6;
    --ai-amber: #E7B45E; --ai-amber-soft: rgba(231,180,94,.15); --ai-amber-text: #F0C887;
    --ai-red: #F0726E; --ai-red-soft: rgba(240,114,110,.15); --ai-red-text: #F5A09D;
    --ai-accent: #9A8BF5; --ai-accent-soft: rgba(154,139,245,.16); --ai-accent-text: #BCB2FA;
    --ai-gray: #5B6470;
  }

  /* Smooth theme transition (200ms cross-fade across all token-using elements).
     B1 Rev 2: box-shadow REMOVED from transition list — was causing hover lag
     on suggestion cards (box-shadow change on :hover transitioned through 200ms
     making hover feel sluggish). Only color/background/border tween.
     M3 Rev 2: linear-gradient CSS values CANNOT transition smoothly between
     light/dark gradient definitions — they snap. Accepted for hero text + KPI
     gradient values + rank-badge gradients + accent overlay. background-image
     intentionally OMITTED from transition list (animates poorly + compositor
     cost on every hover). */
  .ai-themed,
  .ai-themed * {
    transition: background-color 200ms var(--ai-ease),
                color 200ms var(--ai-ease),
                border-color 200ms var(--ai-ease);
  }

  /* Hide visual scrollbars on AI Space pages — PR-POLISH3 2026-05-23.
     User feedback: page + nested-list scrollbars cluttered the modern
     dashboard look (Stripe / Linear hide them entirely). Scroll behavior
     is fully preserved — only the visible track + thumb are hidden.
     Scoped strictly under .ai-themed so the rest of the CRM (which still
     relies на visible scrollbars для accessibility cues) is untouched.
     Trade-off accepted: users lose scroll-position indicator на this page.
     Browser support: WebKit/Chromium через ::-webkit-scrollbar pseudo;
     Firefox через scrollbar-width (must live on the scrolling element itself,
     not the ancestor — applied via .ai-themed cascade below);
     Edge legacy через -ms-overflow-style. */
  .ai-themed *::-webkit-scrollbar,
  .ai-themed::-webkit-scrollbar {
    width: 0 !important;
    height: 0 !important;
    display: none;
  }
  .ai-themed,
  .ai-themed * {
    scrollbar-width: none;          /* Firefox */
    -ms-overflow-style: none;       /* Edge legacy */
  }
  /* FIX4 2026-05-23: ALSO hide page-level scrollbars (body + html + #root)
     when AI Space page active. .ai-themed lives INSIDE body, so .ai-themed *
     selector doesn't catch body's own scrollbar (which scrolls the whole page).
     Use body class toggled by useEffect on InjectionsPage mount/unmount. */
  body.ai-space-active,
  body.ai-space-active #root,
  html:has(body.ai-space-active) {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  body.ai-space-active::-webkit-scrollbar,
  body.ai-space-active #root::-webkit-scrollbar,
  html:has(body.ai-space-active)::-webkit-scrollbar {
    width: 0 !important;
    height: 0 !important;
    display: none;
  }

  /* PR-INJ-CHART (2026-05-23): InjectionTrendChart bar hover state.
     Default fill = --ai-bar-neutral grey, hover lights up к brand accent. */
  .ai-themed .inj-bar {
    transition: fill 0.15s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
  }
  .ai-themed .inj-bar:hover {
    fill: var(--ai-accent) !important;
  }

  /* Premium rise animation for modals (2026-06-18) */
  @keyframes ai-rise {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: none; }
  }
  @keyframes ai-pulse-dot {
    50% { box-shadow: 0 0 0 8px color-mix(in srgb, var(--ai-inj) 6%, transparent); }
  }
  @media (prefers-reduced-motion: reduce) {
    .ai-card-hover { transition: none !important; transform: none !important; }
    .ai-modal-rise { animation: none !important; }
  }
`;
